import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const defaultDirection = (wt) => (wt === "general" || wt === "hazardous" ? "max" : "min");

export const StreamGoalsDialog = ({ open, onOpenChange, clientId, streams, targets, onSaved }) => {
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const next = {};
    streams.forEach((s) => {
      const t = targets.find((x) => x.stream_key === s.key);
      next[s.key] = { kg: t ? String(t.target_kg) : "", direction: t?.direction || defaultDirection(s.waste_type) };
    });
    setDraft(next);
  }, [open, streams, targets]);

  const update = (key, patch) => setDraft((d) => ({ ...d, [key]: { ...d[key], ...patch } }));

  const save = async () => {
    const shownKeys = new Set(streams.map((s) => s.key));
    const kept = targets
      .filter((t) => !shownKeys.has(t.stream_key))
      .map(({ stream_key, stream_name, target_kg, direction }) => ({ stream_key, stream_name, target_kg, direction }));
    const payload = [...kept, ...streams
      .filter((s) => Number(draft[s.key]?.kg) > 0)
      .map((s) => ({ stream_key: s.key, stream_name: s.name, target_kg: Number(draft[s.key].kg), direction: draft[s.key].direction }))];
    setSaving(true);
    try {
      const r = await api.put("/stream-targets", { client_id: clientId, targets: payload });
      onSaved(r.data);
      toast.success("Monthly goals saved");
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save goals");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="stream-goals-dialog">
        <DialogHeader>
          <DialogTitle>Monthly goals per stream</DialogTitle>
          <DialogDescription>Leave a goal empty to remove it. "At least" suits recycling; "At most" suits landfill or hazardous waste.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[55vh] overflow-y-auto divide-y" style={{ borderColor: "rgba(10,10,10,0.08)" }}>
          {streams.map((s) => (
            <div key={s.key} className="py-3 grid grid-cols-12 gap-3 items-center" data-testid={`goal-row-${s.key}`}>
              <div className="col-span-5 text-sm truncate" title={s.name}>{s.name}</div>
              <select
                className="hbt-input text-xs col-span-4"
                value={draft[s.key]?.direction || "min"}
                onChange={(e) => update(s.key, { direction: e.target.value })}
                data-testid={`goal-direction-${s.key}`}
              >
                <option value="min">At least</option>
                <option value="max">At most</option>
              </select>
              <div className="col-span-3 flex items-center gap-1">
                <input
                  type="number" min="0" step="any" placeholder="—"
                  className="hbt-input text-xs w-full"
                  value={draft[s.key]?.kg ?? ""}
                  onChange={(e) => update(s.key, { kg: e.target.value })}
                  data-testid={`goal-kg-${s.key}`}
                />
                <span className="text-xs text-neutral-500">kg</span>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <button type="button" className="btn-outline" onClick={() => onOpenChange(false)} data-testid="goals-cancel-btn">Cancel</button>
          <button type="button" className="btn-primary" disabled={saving} onClick={save} data-testid="goals-save-btn">
            {saving ? "Saving…" : "Save goals"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
